import { useState, useRef, useEffect } from 'react';
import * as api from '../api/storageApi';

interface Props {
  prefix: string;
  onClose: () => void;
  onDone: () => void;
}

export default function NewFolderModal({ prefix, onClose, onDone }: Props) {
  const [name, setName]       = useState('');
  const [error, setError]     = useState('');
  const [saving, setSaving]   = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();

    if (!trimmed) {
      setError('El nombre no puede estar vacío');
      return;
    }

    setSaving(true);
    try {
      // Validar duplicados bajo el mismo prefijo
      const currentPrefix = prefix ? prefix.replace(/\/$/, '') : '';
      const { folders } = await api.listFiles(currentPrefix, 1000);
      const normalized = trimmed.toLowerCase();
      const exists = folders.some(f => f.name.toLowerCase() === normalized);
      if (exists) {
        setError('Ya existe una carpeta con ese nombre en esta ubicación');
        return;
      }

      const path = currentPrefix ? `${currentPrefix}/${trimmed}` : trimmed;
      await api.createFolder(path);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al crear carpeta');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block feebbo-modal" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,20,0.7)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-folder-plus me-2 text-warning" />
              Nueva carpeta
            </h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <label className="form-label fw-medium" style={{ color: 'var(--feebbo-muted)' }}>Nombre de la carpeta</label>
              <input
                ref={inputRef}
                className={`form-control feebbo-input ${error ? 'is-invalid' : ''}`}
                placeholder="ej. contratos-2025"
                value={name}
                onChange={e => { setName(e.target.value); setError(''); }}
                disabled={saving}
              />
              {error && <div className="invalid-feedback" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
              {prefix && (
                <p className="text-body-secondary small mt-2 mb-0">
                  Se creará en: <code>{prefix}/{name || '…'}</code>
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-feebbo-outline" onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-feebbo" disabled={saving || !name.trim()}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Creando…</> : 'Crear carpeta'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
